import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * A custom hook for managing state history (undo/redo) with debounce support.
 * @param {*} initialState The initial state.
 * @param {Object} options Configuration options
 * @param {number} options.limit Maximum number of history entries to keep (default: 50)
 * @param {number} options.debounceMs Debounce timeout in milliseconds (default: 500)
 * @param {boolean} options.enableKeyboardShortcuts Enable Ctrl+Z/Ctrl+Y shortcuts (default: true)
 * @returns An object with the current state, functions to update it, undo, redo, and flags indicating if undo/redo is possible.
 */
export const useHistory = (initialState, { limit = 100, debounceMs = 800, enableKeyboardShortcuts = true } = {}) => {
    const [history, setHistory] = useState({
        past: [],
        present: initialState,
        future: [],
    });
    
    // Ref to track the last time a history entry was added
    const lastUpdateTimeRef = useRef(0);

    const canUndo = history.past.length > 0;
    const canRedo = history.future.length > 0;

    // The main state setter. This is what you call to create a new history entry.
    const setState = useCallback((newStateOrUpdater, skipHistory) => {
        setHistory(currentHistory => {
            const newPresent = typeof newStateOrUpdater === 'function'
                ? newStateOrUpdater(currentHistory.present)
                : newStateOrUpdater;

            // If the new state is the same as the current one, do nothing
            if (newPresent === currentHistory.present) {
                return currentHistory;
            }

            // skipHistory=true: update state but don't add current state to history
            // (used for external syncs like loading shapes from props)
            if (skipHistory) {
                return {
                    past: [],
                    present: newPresent,
                    future: [],
                };
            }

            const now = Date.now();
            const shouldAddToHistory = now - lastUpdateTimeRef.current >= debounceMs;
            
            if (shouldAddToHistory) {
                // console.log('updating history');
                lastUpdateTimeRef.current = now;
                
                const newPast = [...currentHistory.past, currentHistory.present];

                // Enforce the history limit
                if (newPast.length > limit) {
                    newPast.splice(0, newPast.length - limit);
                }
                
                return {
                    past: newPast,
                    present: newPresent,
                    future: [], // Clear future on new action
                };
            } else {
                // console.log('updating state without history (debounced)');
                return {
                    ...currentHistory,
                    present: newPresent,
                    future: [], // Still clear future on new action
                };
            }
        });
    }, [debounceMs, limit]);

    const undo = useCallback(() => {
        if (!canUndo) return;
        
        // Reset debounce timer on undo/redo
        lastUpdateTimeRef.current = 0;
        
        setHistory(currentHistory => {
            const previous = currentHistory.past[currentHistory.past.length - 1];
            const newPast = currentHistory.past.slice(0, currentHistory.past.length - 1);
            return {
                past: newPast,
                present: previous,
                future: [currentHistory.present, ...currentHistory.future],
            };
        });
    }, [canUndo]);

    const redo = useCallback(() => {
        if (!canRedo) return;
        
        // Reset debounce timer on undo/redo
        lastUpdateTimeRef.current = 0;
        
        setHistory(currentHistory => {
            const next = currentHistory.future[0];
            const newFuture = currentHistory.future.slice(1);
            return {
                past: [...currentHistory.past, currentHistory.present],
                present: next,
                future: newFuture,
            };
        });
    }, [canRedo]);

    // Keyboard shortcuts for undo/redo
    useEffect(() => {
        if (!enableKeyboardShortcuts) return;

        const handleKeyDown = (e) => {
            // Ignore shortcuts when typing in input fields
            const targetTagName = e.target.tagName.toLowerCase();
            const targetType = (e.target.type || '').toLowerCase();
            if ((targetTagName === 'input' && targetType !== 'checkbox') || targetTagName === 'textarea' || targetTagName === 'select') {
                return;
            }

            const key = e.key.toLowerCase();

            // Ctrl+Shift+Z for Redo (prioritize this before Ctrl+Z)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && key === 'z') {
                e.preventDefault();
                if (canRedo) {
                    redo();
                }
                return;
            }

            // Ctrl+Z for Undo (Cmd+Z on Mac)
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && key === 'z') {
                e.preventDefault();
                if (canUndo) {
                    undo();
                }
                return;
            }

            // Ctrl+Y for Redo (Cmd+Y on Mac)
            if ((e.ctrlKey || e.metaKey) && key === 'y') {
                e.preventDefault();
                if (canRedo) {
                    redo();
                }
                return;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [enableKeyboardShortcuts, canUndo, canRedo, undo, redo]);

    return { state: history.present, setState, undo, redo, canUndo, canRedo };
};